import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { ToggleSwitch } from '@liveauctioneers/hammer-ui-core/toggleSwitch';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { LIVE_AUCTIONEERS_WINNING_BIDDER_SOURCE } from '@/types/search/searchQuery';
import { FilterAction } from '@/types/SearchFacet';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { modifyParamsRemovePage, parseQueryParams } from '@/utils/queryParams';

const INFO_TOOLTIP_ID = 'sold-on-liveauctioneers-info';

const tooltipMiddlewares = [
    {
        fn: ({ x, y }: { x: number; y: number }) => ({ x, y: y + 10 }),
        name: 'offset',
    },
];

type Props = { isAllFilters?: boolean };

export const SoldOnLiveAuctioneersFilter = ({ isAllFilters }: Props) => {
    const formatMessage = useAppFormatMessage();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const isOn = String(queryParams.winningBidderSource ?? '') === String(LIVE_AUCTIONEERS_WINNING_BIDDER_SOURCE);
    const label = String(formatMessage(['searchPage', 'soldOnLiveAuctioneers']));
    const tooltip = String(formatMessage(['searchPage', 'soldOnLiveAuctioneersTooltip']));

    const apply = (nextOn: boolean) => {
        const nextParams = { ...queryParams };
        if (nextOn) {
            nextParams.winningBidderSource = String(LIVE_AUCTIONEERS_WINNING_BIDDER_SOURCE);
        } else {
            delete nextParams.winningBidderSource;
        }
        if (!isAllFilters) {
            trackQuickFilterPillClicked(QuickFilterName.SoldOnLiveAuctioneers);
        }
        navigateWithParams({
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : undefined,
            filterAction: nextOn
                ? FilterAction.SoldOnLiveAuctioneersChanged
                : FilterAction.SoldOnLiveAuctioneersRemoved,
            params: modifyParamsRemovePage(nextParams),
            preventScrollReset: true,
        });
    };

    if (isAllFilters) {
        return (
            <div className="flex w-full items-center justify-between gap-2xs border-t border-border-secondary pr-xs">
                <div className="flex items-center gap-2xs">
                    <H5>{label}</H5>
                    <Info
                        aria-label={tooltip}
                        color="primary"
                        id={INFO_TOOLTIP_ID}
                        size="xs"
                    />
                    <Tooltip
                        anchorSelect={`#${INFO_TOOLTIP_ID}`}
                        globalCloseEvents={{ scroll: true }}
                        middlewares={tooltipMiddlewares}
                        place="bottom"
                    >
                        <BodySecondary>{tooltip}</BodySecondary>
                    </Tooltip>
                </div>
                <ToggleSwitch
                    aria-label={label}
                    isToggled={isOn}
                    onToggleChange={(nextOn) => {
                        apply(Boolean(nextOn));
                    }}
                />
            </div>
        );
    }

    return (
        <Chip
            id="sold-on-liveauctioneers"
            onClick={() => {
                apply(!isOn);
            }}
            selected={isOn}
        >
            {label}
        </Chip>
    );
};
